import { computed, inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { FeatureFlagConfigurationState, NavigationItem } from '@gea/digital-ui-lib';
import { ROUTE_NAMES } from '@brc/route-names';
import { OrganizationsStore } from '@shared/state';
import { FEATURE_FLAG } from '@shared/utils';
import { isAdmin, isOrganizationRestricted } from '@shared/helpers';

@Injectable({
  providedIn: 'root',
})
export class UserPermissionsService {
  private readonly store = inject(Store);
  private readonly selectedOrganization = inject(OrganizationsStore).selected;
  private readonly featureConfiguration = this.store.selectSignal(FeatureFlagConfigurationState.features);

  private readonly features = computed<ReadonlySet<string>>(
    () =>
      new Set(
        Object.entries(this.featureConfiguration() ?? {})
          .filter(([, enabled]) => enabled)
          .map(([feature]) => feature)
      )
  );

  getActiveTab(currentRoute: string): NavigationItem | undefined {
    const tabs = this.tabs();
    const organizationId = this.selectedOrganization()?.organization?.id;
    return (
      tabs.find((tab) => tab.route?.startsWith(currentRoute)) ??
      tabs.find((tab) => tab.route === `${ROUTE_NAMES.FAULT_ANALYSIS}/${organizationId}`)
    );
  }

  readonly tabs = computed(() => {
    const selected = this.selectedOrganization();
    if (!selected?.organization) {
      return [];
    }

    const { permission, organization } = selected;
    const baseTabs = isAdmin(permission)
      ? this.adminTabs()
      : isOrganizationRestricted(organization)
        ? this.tierTabs()
        : this.nonAdminTabs();
    const featureRoutes: ROUTE_NAMES[] = [];

    if (this.features().has(FEATURE_FLAG.LICENSING)) {
      featureRoutes.push(ROUTE_NAMES.LICENSING);
    }

    featureRoutes.push(ROUTE_NAMES.REMOTE_ACCESS);

    const featureTabs = featureRoutes
      .reduce<NavigationItem[]>((tabs, route) => tabs.concat(this.selectTabs([route])), [])
      .filter((featureTab) => !baseTabs.some((tab) => tab.route === featureTab.route));
    return [...baseTabs, ...featureTabs];
  });

  private readonly allTabs = computed(() => {
    const organizationId = this.selectedOrganization()?.organization?.id;
    if (organizationId == null) {
      return [];
    }

    return [
      {
        label: 'TAB_CONTAINER.TABS.OVERVIEW',
        route: `${ROUTE_NAMES.OVERVIEW}/${organizationId}`,
        icon: 'candlestick-chart',
      },
      {
        label: 'TAB_CONTAINER.TABS.REMOTE_ACCESS',
        route: `${ROUTE_NAMES.REMOTE_ACCESS}/${organizationId}`,
        icon: 'hierarchy-53',
      },
      {
        label: 'TAB_CONTAINER.TABS.SYSTEM_HEALTH',
        route: `${ROUTE_NAMES.SYSTEM_HEALTH}/${organizationId}`,
        icon: 'add-fav',
      },
      {
        label: 'TAB_CONTAINER.TABS.FAULT_ANALYSIS',
        route: `${ROUTE_NAMES.FAULT_ANALYSIS}/${organizationId}`,
        icon: 'candlestick-chart',
      },
      {
        label: 'TAB_CONTAINER.TABS.TROUBLESHOOTING',
        route: `${ROUTE_NAMES.TROUBLESHOOTING}/${organizationId}`,
        icon: 'wrench',
      },
      {
        label: 'TAB_CONTAINER.TABS.FAULT_RECOMMENDATIONS',
        route: ROUTE_NAMES.FAULT_RECOMMENDATIONS,
        icon: 'digital-key',
      },
      {
        label: 'TAB_CONTAINER.TABS.MACHINE_OVERVIEW',
        route: `${ROUTE_NAMES.MACHINE_OVERVIEW}/${organizationId}`,
        icon: 'grid-interface',
      },
      { label: 'TAB_CONTAINER.TABS.SETTINGS', route: `${ROUTE_NAMES.SETTINGS}/${organizationId}`, icon: 'agenda' },
      { label: 'TAB_CONTAINER.TABS.LICENSING', route: `${ROUTE_NAMES.LICENSING}/${organizationId}`, icon: 'notes' },
      {
        label: 'TAB_CONTAINER.TABS.MODEL_FEEDBACKS',
        route: ROUTE_NAMES.MODEL_FEEDBACKS,
        icon: 'digital-key',
      },
    ];
  });

  private readonly nonAdminTabs = computed(() => {
    return this.selectTabs([
      ROUTE_NAMES.SYSTEM_HEALTH,
      ROUTE_NAMES.FAULT_ANALYSIS,
      ROUTE_NAMES.TROUBLESHOOTING,
      ROUTE_NAMES.MACHINE_OVERVIEW,
      ROUTE_NAMES.SETTINGS,
    ]);
  });

  readonly adminTabs = computed(() => {
    return this.selectTabs([
      ROUTE_NAMES.SYSTEM_HEALTH,
      ROUTE_NAMES.FAULT_ANALYSIS,
      ROUTE_NAMES.TROUBLESHOOTING,
      ROUTE_NAMES.FAULT_RECOMMENDATIONS,
      ROUTE_NAMES.MACHINE_OVERVIEW,
      ROUTE_NAMES.SETTINGS,
      ROUTE_NAMES.MODEL_FEEDBACKS,
    ]);
  });

  private readonly tierTabs = computed(() => {
    return this.selectTabs([ROUTE_NAMES.OVERVIEW, ROUTE_NAMES.REMOTE_ACCESS]);
  });

  private selectTabs(indices: ROUTE_NAMES[]): NavigationItem[] {
    return this.allTabs().filter((tab) => {
      const route = tab.route?.split('/')[0];
      return route && indices.includes(route as ROUTE_NAMES);
    });
  }
}
